import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/api/auth.service';
import { refreshOnce, setSessionEndedHandler } from '@/services/api/client';
import { getAccessToken } from '@/services/api/tokens';
import { useAppDispatch } from '@/store/hooks';
import { restoreFinished, sessionEnded, sessionStarted } from '@/store/slices';

/**
 * Turns the refresh cookie back into a live session on load.
 *
 * The access token lives in memory, so a reload always starts with none. The
 * refresh cookie is httpOnly and therefore invisible from here, so "is there a
 * session?" is a question only the server can answer - this asks it before the
 * router renders anything, which is why guards wait on `isRestoring` rather than
 * redirecting immediately.
 *
 * `hasStoredSession` is a hint, not a check: it skips the request for somebody
 * who has never signed in, and being wrong about it costs one failed refresh.
 */
export function SessionRestorer({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const started = useRef(false);

  useEffect(() => {
    /*
     * Strict Mode runs effects twice in development. Restoring twice would fire
     * two refreshes with the same cookie, and the server treats a second
     * exchange of one refresh token as theft - so it would revoke the whole
     * session family and sign the developer out with a security warning every
     * time they reloaded.
     */
    if (started.current) return;
    started.current = true;

    // Lets the network layer end the session without importing the store.
    // The cache goes with it: nothing in it is scoped to a user, so whoever
    // signs in next on this tab must not be shown what it holds.
    setSessionEndedHandler(() => {
      queryClient.clear();
      dispatch(sessionEnded());
    });

    if (!authService.hasStoredSession()) {
      dispatch(restoreFinished());
      return;
    }

    void (async () => {
      let refreshed: boolean;
      try {
        refreshed = await refreshOnce();
      } catch {
        /*
         * The server could not be reached even after retrying - offline, or
         * the API restarting. That says nothing about the session, so the
         * session hint is left alone: this lands on the login page for now,
         * and the next reload tries the cookie again instead of forgetting it.
         */
        dispatch(sessionEnded());
        return;
      }
      if (!refreshed) {
        dispatch(sessionEnded());
        return;
      }

      const token = getAccessToken();
      if (!token) {
        dispatch(sessionEnded());
        return;
      }

      try {
        const user = await authService.me();
        dispatch(sessionStarted({ user, accessToken: token }));
      } catch {
        // The token refreshed but the account is gone or disabled.
        dispatch(sessionEnded());
      }
    })();
  }, [dispatch, queryClient]);

  return <>{children}</>;
}
